import {
  BuiltInSectionKey,
  CustomSectionKey,
  NarrativeSectionKey,
  PacketCustomSection,
  SectionKey,
  customSectionKey,
} from '@marketing-milk/interfaces'

// Section keys are a shared contract now that `hiddenSections` persists them
// (slice 12.5), so they live in `interfaces/` and are re-exported here — the
// import site every packet component already uses.
export type { BuiltInSectionKey, CustomSectionKey, SectionKey }
export { customSectionKey } from '@marketing-milk/interfaces'

export type SectionKind =
  | 'performance'
  | 'narrative'
  | 'client-survey'
  | 'audience'
  | 'brand'
  | 'creative'
  | 'custom'

/**
 * A built-in section. Discriminated on `kind` so the `narrative` variant can
 * type its key as a `NarrativeSectionKey` — both hosts' `case 'narrative':`
 * then narrows for free, with no `Exclude<>` cast hand-enumerating the
 * non-narrative keys (which would break the moment `SectionKey` gained a
 * custom member).
 */
/**
 * The keys that are both a section of the deck and a narrative block. After
 * slice 10 retired Website / Outstanding Needs / Feedback this is just
 * `upcoming-meetings` — the other narrative keys (`creative-strategy`, the four
 * `budget-*`) are blocks hosted inside other sections, never sections of their
 * own.
 */
export type NarrativeBuiltInSectionKey = BuiltInSectionKey & NarrativeSectionKey

export type SectionDef =
  | { kind: 'narrative'; key: NarrativeBuiltInSectionKey; label: string }
  | {
      kind: Exclude<SectionKind, 'narrative' | 'custom'>
      key: BuiltInSectionKey
      label: string
    }

/** An AE-authored slide, wrapped to sit alongside `SectionDef` in the deck. */
export interface CustomSectionDef {
  kind: 'custom'
  key: CustomSectionKey
  label: string
  section: PacketCustomSection
}

export type PacketSection = SectionDef | CustomSectionDef

/**
 * The built-in spine. Six entries — `website`, `outstanding-needs` and
 * `feedback` were retired in slice 10 and are now AE-authored custom slides.
 * Their keys deliberately stay in `narrativeSectionKeySchema`: the stored prose
 * is preserved, and no Builder UI can reach those keys once they leave here.
 */
export const SECTIONS: SectionDef[] = [
  { key: 'performance-review', label: 'Performance Review', kind: 'performance' },
  { key: 'audience-framework', label: 'Audience framework', kind: 'audience' },
  { key: 'brand-standards', label: 'Brand Standards', kind: 'brand' },
  { key: 'creative', label: 'Creative', kind: 'creative' },
  { key: 'client-survey', label: 'Client Survey', kind: 'client-survey' },
  { key: 'upcoming-meetings', label: 'Upcoming Meetings', kind: 'narrative' },
]

/**
 * Gap rank: which built-in a slide follows. `null` sorts before everything; an
 * `afterKey` naming a built-in retired after the slide was written ranks last
 * rather than throwing. Mirrors `gapRank` in the Nest service.
 */
const gapRank = (sections: SectionDef[], afterKey: BuiltInSectionKey | null): number => {
  if (afterKey === null) return -1
  const idx = sections.findIndex(s => s.key === afterKey)
  return idx < 0 ? sections.length : idx
}

/**
 * The deck an AE actually sees: the built-in spine with each gap's custom
 * slides spliced in. Pure — total order is gap rank, then array order within
 * the gap, exactly as the server computes it.
 */
export const mergeSections = (
  sections: SectionDef[],
  customSections: PacketCustomSection[]
): PacketSection[] => {
  const byGap = new Map<number, CustomSectionDef[]>()
  for (const section of customSections) {
    const rank = gapRank(sections, section.afterKey)
    const defs = byGap.get(rank) ?? []
    defs.push({
      kind: 'custom',
      key: customSectionKey(section.id),
      label: section.title,
      section,
    })
    byGap.set(rank, defs)
  }

  const merged: PacketSection[] = [...(byGap.get(-1) ?? [])]
  sections.forEach((section, i) => {
    merged.push(section)
    merged.push(...(byGap.get(i) ?? []))
  })
  merged.push(...(byGap.get(sections.length) ?? []))
  return merged
}

/**
 * The deck the client actually sees, and the numbering Present uses. Named
 * for that audience deliberately — it used to also be Edit's numbering
 * source, back when the two were required to match number-for-number
 * ("05 Creative" in Edit becoming "04" for the client). DRI-39 broke that:
 * Edit now numbers a linkless client survey too (see `editorNavSections`), so
 * while a packet is unconfigured its Edit numbers run one ahead of what
 * Present shows for everything after the survey card — a deliberate,
 * temporary divergence that resolves itself once the link exists.
 *
 * Two independent rules, deliberately kept separate:
 *
 * 1. **Derived** — a packet with no Salesforce survey link has no survey slide
 *    here. Derived from the stored link, never from AE state: a toggle would
 *    let someone unhide a survey slide with no link behind it and show the
 *    client a dead CTA.
 * 2. **AE state** — slides the AE hid, built-in or custom (slice 12.5).
 */
export const visiblePacketSections = (
  all: PacketSection[],
  hiddenSections: SectionKey[],
  surveyLink: string | null
): PacketSection[] => {
  const hidden = new Set<SectionKey>(hiddenSections)
  return all.filter(s => {
    if (s.key === 'client-survey' && !surveyLink) return false
    return !hidden.has(s.key)
  })
}

/**
 * Edit's own deck and numbering source (DRI-39) — every AE-visible row,
 * including a linkless client survey. It needs a real, numbered, clickable
 * row so an AE can reach the warning/fix from the TOC rather than scrolling
 * to find an unnumbered card; `EditorTOC` renders a "(hidden)" tag on it
 * instead of leaving it out of the count. See `visiblePacketSections` for the
 * numbering divergence this causes with Present until the link is fixed.
 */
export const editorNavSections = (
  all: PacketSection[],
  hiddenSections: SectionKey[]
): PacketSection[] => {
  const hidden = new Set<SectionKey>(hiddenSections)
  return all.filter(s => !hidden.has(s.key))
}
